import { Address, IOlavEntry } from '../address/address.model';
import {
  CommonMappingData,
  IAktionen,
  IBankDetails,
  IBulletConfirmationStatusChangeResponse,
  IERechnungsInfo,
  ITarif,
} from '../integrationlayer/api.model';
import { IdCard, IOrderDetailsInfo, ORDER_DETAILS_VERSION, toReferrencedFiles } from '../order/order.model';
import { IFileReferenceModel, IUploadResult } from '../upload/file.model';
import { PreisIndikation, TariffSelectionCardData } from '../availabilities/availabilities.model';
import { CommonPDFLinks } from '../pdf-links/pdf-links.model';
import { PriceSheet } from '../price-sheet/price-sheet.model';

export type RelocationConfirmationStatusResponse = IBulletConfirmationStatusChangeResponse;

export type RelocationPDFLinks = CommonPDFLinks;

export interface IRelocationData {
  relocationFromAddress: Address;
  relocationToOlav: IOlavEntry;
  relocationToAddress: Address;
  registrationDate: string;
  deregistrationDate: string;
  relocationDetails: IOrderDetailsInfo[];
  idCard: IdCard;
  uploadedFiles: IUploadResult;
  useSubstituteDetails: boolean;
  pdfLinks: RelocationPDFLinks;
  priceSheets: PriceSheet[];
  noOnlineCategories?: string[];
  selectedTariffs?: TariffSelectionCardData[];
  eRechnungChangeIndices?: number[];
  sepaChangeIndices?: number[];
}

export interface IRelocationDataDTO {
  selectedOlav: IOlavEntry;
  oldAddress: Address;
  newAddress: Address;
  deregistrationDate: string;
  registrationDate: string;
  relocationSubmittedAt?: string;
  orderDetailsInfo: IOrderDetailsInfo[];
  idcard: IdCard;
  referencedFiles: IFileReferenceModel[];
  useSubstituteDetails: boolean;
  pdfLinks: RelocationPDFLinks;
  noOnlineCategories?: string[];
  noLongerAvailableCategories?: string[];
  priceSheets: PriceSheet[];
  version?: string;
  selectedTariffs?: TariffSelectionCardData[];
  eRechnungChangeIndices?: number[];
  sepaChangeIndices?: number[];
  voucherInfo?: IAktionen;
}

// tslint:disable-next-line: no-empty-interface
export type IRelocationMappingData = CommonMappingData;
// tslint:disable-next-line: no-empty-interface
export type IRelocationConfirmationStatusResponse = IBulletConfirmationStatusChangeResponse;

export interface IRelocationDTOV2 {
  oldAddress: Address;
  newAddressOlav: IOlavEntry;
  sepaMandat?: IBankDetails;
  eRechnungsInfo?: IERechnungsInfo;
  registrationDate: string;
  deregistrationDate: string;
  id?: number;
  mappingData?: IRelocationMappingData[];
  grantedMarketingPermission?: boolean;
  priceIndications?: PreisIndikation[];
  idCard: IdCard;
  referencedFiles: IFileReferenceModel[];
  pdfLinks: RelocationPDFLinks;
  orderedAt?: string;
  processStatus?: string;
  invoiceAddressOlav?: IOlavEntry;
  noOnlineCategories?: string[];
  noLongerAvailableCategories?: string[];
  version?: string;
  selectedTariffs?: TariffSelectionCardData[];
  voucherInfo: IAktionen;
}

export const newRelocationDTOV1 = (relocationData: IRelocationData): IRelocationDataDTO => {
  const dto = {
    selectedOlav: relocationData?.relocationToOlav,
    oldAddress: relocationData?.relocationFromAddress,
    newAddress: relocationData?.relocationToAddress,
    deregistrationDate: relocationData?.deregistrationDate,
    registrationDate: relocationData?.registrationDate,
    orderDetailsInfo: relocationData?.relocationDetails,
    idcard: relocationData?.idCard,
    referencedFiles: relocationData?.uploadedFiles ? toReferrencedFiles(relocationData?.uploadedFiles.uploadedFiles) : null,
    useSubstituteDetails: relocationData?.useSubstituteDetails,
    pdfLinks: relocationData?.pdfLinks,
    noOnlineCategories: relocationData?.noOnlineCategories,
    selectedTariffs: relocationData?.selectedTariffs,
    eRechnungChangeIndices: relocationData?.eRechnungChangeIndices,
    sepaChangeIndices: relocationData?.sepaChangeIndices,
    priceSheets: relocationData?.priceSheets,
  };

  return {
    ...dto,
    orderDetailsInfo: relocationData?.relocationDetails.map(d => {
      if (!d.partnerDetails?.physischePerson?.geburtsdatum?.includes('T00:00Z')) return d;

      return {
        ...d,
        partnerDetails: {
          ...d.partnerDetails,
          physischePerson: {
            ...d.partnerDetails.physischePerson,
            geburtsdatum: d.partnerDetails.physischePerson.geburtsdatum.replace('T00:00Z-', ''),
          },
        },
      };
    }),
  };
};

export const newRelocationDetailsInfo = (
  relocationStatus: 'Abmeldung' | 'Anmeldung',
  _zustelladresse: 'from' | 'to' | '',
  _category: string,
  _customerNumber: string,
  _accountNUmber: string,
  _tariff: ITarif,
  _availabilityDetailsFernwaerme: string[],
): IOrderDetailsInfo => {
  return {
    apiVersion: ORDER_DETAILS_VERSION,
    produktDetails: null,
    invoiceAddress: null,
    eRechnungInfo: null,
    bezahlartIcon: null,
    bezahlartText: null,
    status: relocationStatus,
    zustelladresse: _zustelladresse,
    category: _category,
    customerNumber: _customerNumber,
    accountNumber: _accountNUmber,
    physischePerson: null,
    tariff: _tariff,
    availabilityDetailsFernwaerme: _availabilityDetailsFernwaerme,
  };
};

export interface OSSRelocationData {
  customer: OssCustomer | OssCustomerOld;
  address: OSSAddress;
}

export interface OssCustomerIdentification {
  birthDate: string;
  oldAddress: OSSAddress;
}

export interface OssCustomer {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  identification: OssCustomerIdentification;
}

export interface OssCustomerOld {
  //TODO: remove with MWE-15853
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  birthDate: string;
}

export interface OSSAddress {
  streetName: string;
  houseNumber: string;
  houseNumberExtension?: string;
  postalCode: string;
  cityName: string;
  isoCountryCode: string;
  additionalAddressLines?: string[];
}

export interface OssRelocationResponse {
  id: string;
  iamUserId: string;
  customer: OssCustomer;
  address: OSSAddress;
  createdAt: string;
}

export interface OssRelocationResponseOld {
  //TODO: remove with MWE-15853
  id: string;
  iamUserId: string;
  customer: OssCustomerOld;
  address: OSSAddress;
  createdAt: string;
}
